import React, { useState, useEffect } from 'react';
import { customerService } from '../../services/customerService';

const CustomerSearch = ({ onCustomerSelect, selectedCustomer }) => {
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Resetuj query gdy selectedCustomer zmienia się na null (po wyczyszczeniu koszyka)
  useEffect(() => {
    if (!selectedCustomer) {
      setQuery('');
      setCustomers([]);
      setShowResults(false);
    }
  }, [selectedCustomer]);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setCustomers([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchCustomers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const searchCustomers = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Kasa obsluguje wylacznie odbiorcow - bez tego filtra kasjer widzi
      // dostawcow (np. SFD), ktorzy po scaleniu siedza w tej samej kartotece
      const response = await customerService.searchCustomers(query, 10, 'odbiorca');

      if (response.success) {
        const customers = response.data.customers || [];
        setCustomers(customers);
        setShowResults(true);
      } else {
        setError(response.message || 'Błąd wyszukiwania');
        setCustomers([]);
      }
    } catch (err) {
      console.error('Błąd wyszukiwania klientów:', err);
      setError(err.message || 'Błąd wyszukiwania klientów');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    onCustomerSelect(customer);
    setQuery(customer.nazwa_firmy || customer.name || `${customer.imie || ''} ${customer.nazwisko || ''}`.trim() || '');
    setShowResults(false);
  };

  const clearSelection = () => {
    setQuery('');
    setCustomers([]);
    setShowResults(false);
    onCustomerSelect(null);
  };

  return (
    <div className="customer-search">
      <div className="form-group">
        <label className="form-label">
          👤 Wyszukaj klienta
        </label>
        
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Wprowadź nazwę, telefon lub email klienta..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setShowResults(true)}
          />
          
          {selectedCustomer && (
            <button
              type="button"
              onClick={clearSelection}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error">
          ⚠️ {error}
        </div>
      )}

      {loading && (
        <div className="loading">
          🔍 Wyszukiwanie klientów...
        </div>
      )}

      {showResults && customers.length > 0 && (
        <div className="search-results" style={{
          position: 'absolute',
          top: '100%',
          left: '0',
          right: '0',
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          {customers.map((customer) => (
            <div
              key={customer.id}
              onClick={() => handleCustomerSelect(customer)}
              style={{
                padding: '12px',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--bg)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--card-bg)'}
            >
              <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                {customer.nazwa_firmy || customer.name || `${customer.imie || ''} ${customer.nazwisko || ''}`.trim() || `#${customer.id}`}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                {customer.phone && `📞 ${customer.phone}`}
                {customer.phone && customer.email && ' • '}
                {customer.email && `📧 ${customer.email}`}
              </div>
              {customer.address && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  📍 {customer.address}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showResults && customers.length === 0 && !loading && query.length >= 2 && (
        <div className="search-results" style={{
          position: 'absolute',
          top: '100%',
          left: '0',
          right: '0',
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1000,
          padding: '12px',
          textAlign: 'center',
          color: 'var(--text-secondary)'
        }}>
          🔍 Nie znaleziono klientów dla zapytania "{query}"
        </div>
      )}

      {selectedCustomer && (
        <div className="selected-customer" style={{
          marginTop: '10px',
          padding: '12px',
          background: 'var(--success-light)',
          border: '1px solid var(--success-light)',
          borderRadius: '4px'
        }}>
          <div style={{ fontWeight: '500', color: 'var(--success)' }}>
            ✅ Wybrany klient: {selectedCustomer.nazwa_firmy || selectedCustomer.name || `${selectedCustomer.imie || ''} ${selectedCustomer.nazwisko || ''}`.trim() || `#${selectedCustomer.id}`}
          </div>
          {selectedCustomer.phone && (
            <div style={{ fontSize: '14px', color: 'var(--success)' }}>
              📞 {selectedCustomer.phone}
            </div>
          )}
          {selectedCustomer.email && (
            <div style={{ fontSize: '14px', color: 'var(--success)' }}>
              📧 {selectedCustomer.email}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomerSearch);
